import { rem } from 'polished'
import React, { useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { DATA_TEST_ID } from 'shared-constants/build/testIds'
import styled from 'styled-components'
import { Button, BUTTON_INTENT, BUTTON_VARIANT } from 'components/Button'
import { Card, CARD_ELEVATION, CARD_PADDING_SIZE } from 'components/Card'
import { Box, Flex } from 'components/Layout'
import { Loader, LOADER_SIZE } from 'components/Loader'
import NoData from 'components/NoData'
import { H4, Text } from 'components/Typography'
import { REVENUE_PERIOD_STATES } from 'constants/common'
import { useUserAuth } from 'providers/userAuth'
import { COLOR, FONT_SIZE, FONT_WEIGHT, SPACE } from 'Theme'
import { useMedia } from 'useMedia'
import {
  BAR_CHART_CUP_RADIUS,
  BAR_SIZE,
  PERIOD_DATE_GROUP_STATES,
  X_AXIS_DIAGONAL_PROPS,
} from '../chartHelpers'
import { useExpertConsultationsQuery } from './hooks'
import { prepareDataForCharts } from './utils'

const WIDGET_AREA_HEIGHT = rem(320)
const CHART_MIN_HEIGHT = rem(260)

const StyledText = styled(Text)`
  white-space: nowrap;
`

export const ConsultationsChartWidget = () => {
  const media = useMedia()
  const { user, convertDateToTimezone } = useUserAuth()
  const [periodDateGroup, setPeriodDateGroup] = useState(
    REVENUE_PERIOD_STATES.THIS_MONTH
  )
  const { isLoading, data: sessionCounts } = useExpertConsultationsQuery({
    expertId: user.id,
    period: PERIOD_DATE_GROUP_STATES[periodDateGroup]({
      expertCreated: convertDateToTimezone(
        new Date(user.expert?.createdAtUtc.isoFormat)
      ),
      now: convertDateToTimezone(new Date()),
    }),
  })

  const { chartData, total } = prepareDataForCharts({
    sessionCounts,
    periodDateGroup,
    convertDateToTimezone,
  })

  return (
    <Card
      flexDirection="column"
      width="100%"
      elevation={CARD_ELEVATION.NONE}
      paddingSize={CARD_PADDING_SIZE.NONE}
      data-test-id={DATA_TEST_ID.CONSULTATION_CHART_WIDGET}
      hasBorders
    >
      <Flex flexDirection="column" p={SPACE.PX_30}>
        <Flex flexDirection="row" flexWrap="wrap" pb={SPACE.PX_15}>
          <Flex flex="1" alignItems="center" flexWrap="nowrap">
            <StyledText
              textTransform="captialize"
              fontSize={{ MOBILE: FONT_SIZE.PX_16, TABLET: FONT_SIZE.PX_20 }}
              fontWeight={FONT_WEIGHT.MEDIUM}
              color={COLOR.GRAYSCALE_1}
            >
              Consultations
            </StyledText>
          </Flex>
          <Flex
            selfAlign="end"
            flexWrap="wrap"
            maxWidth="fit-content"
            mr={`-${SPACE.PX_15}`}
          >
            <Button
              width="auto"
              variant={BUTTON_VARIANT.MINIMAL}
              intent={
                periodDateGroup === REVENUE_PERIOD_STATES.ALL_TIME
                  ? BUTTON_INTENT.PRIMARY
                  : BUTTON_INTENT.NONE
              }
              onClick={() => {
                setPeriodDateGroup(REVENUE_PERIOD_STATES.ALL_TIME)
              }}
            >
              All time
            </Button>
            <Button
              width="auto"
              variant={BUTTON_VARIANT.MINIMAL}
              intent={
                periodDateGroup === REVENUE_PERIOD_STATES.LAST_MONTH
                  ? BUTTON_INTENT.PRIMARY
                  : BUTTON_INTENT.NONE
              }
              onClick={() => {
                setPeriodDateGroup(REVENUE_PERIOD_STATES.LAST_MONTH)
              }}
            >
              Last month
            </Button>
            <Button
              width="auto"
              variant={BUTTON_VARIANT.MINIMAL}
              intent={
                periodDateGroup === REVENUE_PERIOD_STATES.THIS_MONTH
                  ? BUTTON_INTENT.PRIMARY
                  : BUTTON_INTENT.NONE
              }
              onClick={() => {
                setPeriodDateGroup(REVENUE_PERIOD_STATES.THIS_MONTH)
              }}
            >
              This month
            </Button>
          </Flex>
        </Flex>
        {total > 0 ? (
          <>
            <H4 color={COLOR.PRIMARY_1} pb={SPACE.PX_20}>
              {total}
            </H4>
            <Box>
              <ResponsiveContainer width="100%" minHeight={CHART_MIN_HEIGHT}>
                <BarChart data={chartData} margin={0}>
                  <CartesianGrid strokeDasharray="3" vertical={false} />
                  <XAxis
                    dataKey="columnLabel"
                    fontSize={FONT_SIZE.PX_12}
                    {...X_AXIS_DIAGONAL_PROPS}
                  />
                  <YAxis
                    fontSize={FONT_SIZE.PX_14}
                    width={media.MOBILE ? BAR_SIZE.M : BAR_SIZE.L}
                    allowDecimals={false}
                  />
                  <Tooltip
                    cursor={{ opacity: 0.2 }}
                    contentStyle={{ fontSize: FONT_SIZE.PX_14 }}
                    labelStyle={{ fontWeight: FONT_WEIGHT.MEDIUM }}
                    separator=": "
                  />
                  <Bar
                    isAnimationActive={
                      process.env.NEXT_PUBLIC_NODE_ENV !== 'test'
                    }
                    dataKey="count"
                    barSize={media.MOBILE ? BAR_SIZE.S : BAR_SIZE.M}
                    fill={COLOR.PRIMARY_1}
                    radius={BAR_CHART_CUP_RADIUS}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </>
        ) : (
          <Flex
            alignItems="center"
            justifyContent="center"
            height={WIDGET_AREA_HEIGHT}
          >
            {isLoading ? (
              <Loader size={LOADER_SIZE.S} />
            ) : (
              <NoData
                header="No consultations in selected range"
                content="There will be displayed your consultation overview"
              />
            )}
          </Flex>
        )}
      </Flex>
    </Card>
  )
}
